import Component from '@glimmer/component';

import { registerDestructor } from '@ember/destroyable';
import { hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { DEBUG } from '@glimmer/env';
import { cached, tracked } from '@glimmer/tracking';

import classMod from 'rise-ui/helpers/class-mod.js';
import classModConfig from 'rise-ui/helpers/class-mod-config.js';
import emberArray from 'rise-ui/helpers/ember-array.js';
import max from 'rise-ui/helpers/max.js';
import not from 'rise-ui/helpers/not.js';
import passProperties from 'rise-ui/helpers/pass-properties.js';
import onDrag from 'rise-ui/modifiers/on-drag.js';
import onResize from 'rise-ui/modifiers/on-resize.js';
import update from 'rise-ui/modifiers/update.js';
import { animate } from 'rise-ui/utils/animate.js';
import { extentOne } from 'rise-ui/utils/array.js';
import { clearCanvas, createCanvas, getContext } from 'rise-ui/utils/canvas.js';
import { resizeRect, resizeStyleRect, undraggable } from 'rise-ui/utils/dom.js';
import { drawGraphLines } from 'rise-ui/utils/canvas/graph-lines.js';
import { drawGraphAxis } from 'rise-ui/utils/canvas/graph-axis.js';
import { drawNodeSquare } from 'rise-ui/utils/canvas/graph-shapes.js';
import { alpha } from 'rise-ui/utils/color.js';
import { call } from 'rise-ui/utils/function.js';
import { Changes, GraphConfig } from 'rise-ui/utils/graph-config.js';
import { isSet, isUnset } from 'rise-ui/utils/nullish.js';
import { clamp } from 'rise-ui/utils/number.js';
import { ResizeObserver } from 'rise-ui/utils/resize-observer.js';

const features = {
  lineOrder: true,
  alpha: true,
};

const ctxOptions = {
  alpha: features.alpha,
};

const debug = {
  messages: false,
  timing: false,
};
const graphBgColor = '#ccf';
const xAxisBgColor = '#cfc';

const debugConsole = console;
const debugGroup = (...args) => {
  if (DEBUG) {
    if (debug.messages || debug.timing) {
      debugConsole.group(...args);
    }
  }
};
const debugGroupEnd = () => {
  if (DEBUG) {
    if (debug.messages || debug.timing) {
      debugConsole.groupEnd();
    }
  }
};
const debugTime = key => {
  if (DEBUG) {
    if (debug.timing) {
      debugConsole.time(key);
    }
  }
};
const debugTimeEnd = key => {
  if (DEBUG) {
    if (debug.timing) {
      debugConsole.timeEnd(key);
    }
  }
};
const debugLog = (...args) => {
  if (DEBUG) {
    if (debug.messages && args.length) {
      debugConsole.log(...args);
    }
  }
};

const matchClasses = /\S+/g;
function getClasses(classString) {
  return classString && classString.match(matchClasses) || [];
}

let CHANGES = 0;
const CHANGE_RENDER            = ++CHANGES; // Some render is needed
const CHANGE_REDRAW            = ++CHANGES; // Everything needs redraw
const CHANGE_DOMAIN            = ++CHANGES; // Data changed
const CHANGE_PIXEL_INDEX       = ++CHANGES; // Pixel index needs refreshing
const CHANGE_SIZE              = ++CHANGES; // Overall width or height changed
const CHANGE_SIZE_WIDTH        = ++CHANGES; // Overall width changed
const CHANGE_SIZE_HEIGHT       = ++CHANGES; // Overall height changed
const CHANGE_X_AXIS            = ++CHANGES; // X-Axis needs redraw
const CHANGE_LINE_REDRAW       = ++CHANGES; // Lines need redraw
const CHANGE_LINE_META         = ++CHANGES; // Changes to line-meta data

function initChanges(changes) {
  // Drawing skipped unless one of these is set each frame
  changes.changeWith(CHANGE_RENDER,
    CHANGE_REDRAW,
    CHANGE_SIZE,
    CHANGE_LINE_REDRAW,
    CHANGE_LINE_META,
  );

  // Full redraw on domain or size change
  changes.changeWith(CHANGE_REDRAW, CHANGE_DOMAIN, CHANGE_SIZE);

  // Recalculate pixel indexes on domain or width change
  changes.changeWith(CHANGE_PIXEL_INDEX, CHANGE_DOMAIN, CHANGE_SIZE_WIDTH);

  changes.changeWith(CHANGE_SIZE, CHANGE_SIZE_WIDTH, CHANGE_SIZE_HEIGHT);

  // Draw axes once each full redraw
  changes.changeWith(CHANGE_X_AXIS, CHANGE_REDRAW);

  changes.changeWith(CHANGE_LINE_REDRAW, CHANGE_REDRAW);

  return changes;
}

const cursors = {
  handle: 'dragging-slider-handle',
  range: 'dragging-slider-range',
};

// TODO solve padding around scroll control on mobile
// TODO fix canvas PPI on mobile

/**
 * GraphScroll input attributes
 * @name GraphScrollInputs
 * @interface
 * @extends GraphConfigInput
 */
/**
 * Draws a graph with resizable bounds
 * @name GraphScroll
 * @property {GraphScrollInputs} args
 */
export default class GraphScroll extends Component {
  config = new GraphConfig();

  @service('debug/time-series-output') timeSeriesOutputs;

  wrapper = undefined;
  @tracked _data;

  changes = initChanges(new Changes());
  linesZIndex = 100;

  @tracked
  handleWidth = 0;

  outsideClickMoves = true;
  outsideMouseTimout = 500;
  // Number of pixels of dragging outside range to force `size` over `move`
  outsideDragPixels = 5;

  @tracked windowStart;
  @tracked windowEnd;
  @tracked isFineWindow = false; // i.e. animating
  @tracked _fineWindowStart;
  get fineWindowStart() {
    return this.isFineWindow ? this._fineWindowStart : this.windowStart;
  }
  @tracked _fineWindowEnd;
  get fineWindowEnd() {
    return this.isFineWindow ? this._fineWindowEnd : this.windowEnd;
  }

  @action
  didInsertWrapper(wrapper, [ templateConfig ]) {
    this.templateConfig = templateConfig;
    this.wrapper = undraggable(wrapper);
    wrapper.style.position = 'relative';

    wrapper.addEventListener('dblclick', () => {
      this.dragType = null;
      this.startX = this.selectionMin;
      this.endX = this.selectionMax;
      this.updateSliderPositions();
    });

    const { width, height } = this.config;

    const addToWrapper = (el, style = {}) => {
      undraggable(el);
      Object.assign(el.style, style);
      this.wrapper.appendChild(el);
      return el;
    };

    // Create and insert main canvas into wrapper at initial dimensions
    const canvas = this.mainCanvas = undraggable(createCanvas(width, height, '#fff', { alpha: false }));
    wrapper.appendChild(canvas);

    this.bottomAxisCanvas = addToWrapper(createCanvas(0, 0, '#fff', { alpha: false }));
    this.bottomAxisCanvas.classList.add(
      ...getClasses(templateConfig.axisClass),
      ...getClasses(templateConfig.bottomAxisClass)
    );

    // Under all lines in case lines needs background-color
    this.lineBackgroundElement = addToWrapper(document.createElement('div'), {
      position: 'absolute',
      backgroundColor: this.timeSeriesOutputs.colorGraph?.graphBgColor || this.args.backgroundColor,
      top: 0,
      left: 0,
      width: `${width}px`,
      height: `${height}px`,
    });

    this.linesEl = addToWrapper(document.createElement('div'));
    this.linesEl.classList.add(...getClasses(templateConfig.linesClass));
    this.lineCanvases = [];

    const rangeStyle = { position: 'absolute' };
    this.startHandle = undraggable(wrapper.querySelector('[data-slider-handle="start"]'));
    Object.assign(this.startHandle.style, rangeStyle);
    this.endHandle = undraggable(wrapper.querySelector('[data-slider-handle="end"]'));
    Object.assign(this.endHandle.style, rangeStyle);
    this.sliderRange = undraggable(wrapper.querySelector('[data-slider-range]'));
    Object.assign(this.sliderRange.style, rangeStyle);

    // Watch size of wrapper to affect size of canvas and redraw
    this.startWatchingSize();

    registerDestructor(this, () => {
      this.sizeWatcher.disconnect();
      this.sizeWatcher = undefined;
      this.stopAnimation();
      call(this.args.onDestroy, this, this);
    });
  }

  sizeWatcher = undefined;

  // Update graph width/height based on wrapper element size
  startWatchingSize() {
    const { input } = this.config;

    this.sizeWatcher = new ResizeObserver(([ entry ]) => {
      const cbs = entry.contentBoxSize;
      const cr = entry.contentRect;
      const [ width, height ] = cbs ? [ cbs[0].inlineSize, cbs[0].blockSize ] : [ cr.width, cr.height ];
      input.width = width;
      input.height = height;
    });
    this.sizeWatcher.observe(this.wrapper);
  }

  animation = undefined;
  startAnimation() {
    if (this.animation) {
      return;
    }
    this.animation = animate(() => this.draw(), 60);
  }
  stopAnimation() {
    if (this.animation) {
      this.animation = this.animation();
    }
  }

  @action onSourceGraphRectChange() {
    this.changes.set(CHANGE_REDRAW, true);
  }

  @action onLineMetaChange() {
    this.changes.set(CHANGE_LINE_META, true);
  }

  @action onDataKeyColorChange() {
    this.changes.set(CHANGE_LINE_REDRAW, true);
  }

  @action onDataChange() {
    this.changes.set(CHANGE_DOMAIN, true);
  }

  @action onScaleChange() {
    this.changes.set(CHANGE_REDRAW, true);
  }

  @action onConfigChange(value) {
    // console.log('onConfigChange', value);
  }
  @action onWidthChange(value) {
    // console.log('onWidthChange', value);
  }
  @action onHeightChange(value) {
    // console.log('onHeightChange', value);
  }

  @action onSizeChange() {
    let { width, height } = this.config;
    width = width || 0;
    height = height || 0;
    const hasChange = this.changes
      .change(CHANGE_SIZE_WIDTH, width)
      .change(CHANGE_SIZE_HEIGHT, height)
      .changed(CHANGE_SIZE);
    if (!hasChange) {
      return;
    }
    // console.log('onSizeChange');
    if (!width || !height) {
      this.stopAnimation();
      const canvas = this.mainCanvas;
      if (canvas) {
        resizeRect(canvas, width, height, window.devicePixelRatio);
      }
    } else {
      this.setPixelsFromWindow();
      this.updateSliderPositions();
      this.startAnimation();
    }
  }

  @action onSizeChange2() {
    let { width, height } = this.config;
    // console.log('onSizeChange2', width, height);
  }

  @action setWindowFromSliderPositions() {
    let { isFineWindow, start, end, hasStartChanged, hasEndChanged }
      = this.getWindowFromSliderPositions();
    if (hasStartChanged) {
      if (isFineWindow) {
        this.windowStart = start;
      } else {
        this._fineWindowStart = start;
      }
    }
    if (hasEndChanged) {
      if (isFineWindow) {
        this.windowEnd = end;
      } else {
        this._fineWindowEnd = end;
      }
    }
    if (hasStartChanged || hasEndChanged) {
      if (isFineWindow) {
        if (!this.dragType) {
          isFineWindow = this.isFineWindow = false;
        }
        call(this.args.onWindowAnimate, 0, start, end);
      }
      if (!isFineWindow) {
        if (this.dragType) {
          this.isFineWindow = true;
        }
        call(this.args.onWindowChange, 0, start, end);
      }
    }
  }

  getWindowFromSliderPositions() {
    let { startX, endX, isFineWindow } = this;
    if (startX > endX) {
      [ startX, endX ] = [ endX, startX ];
    }
    const { fineWindowStart: oldStart, fineWindowEnd: oldEnd } = this;
    const start = this.getWindowFromSliderPosition(startX);
    const end = this.getWindowFromSliderPosition(endX, true);
    const hasStartChanged = oldStart !== start;
    const hasEndChanged = oldEnd !== end;
    return {
      isFineWindow, startX, endX, start, end,
      hasStartChanged, hasEndChanged,
      hasChanged: hasStartChanged || hasEndChanged,
    };
  }

  getWindowFromSliderPosition(position, end = false) {
    const { graphLeft, graphMargin, xScale } = this.config;
    return xScale.invert(Math[end ? 'ceil' : 'floor'](position - graphLeft - graphMargin));
  }

  setPixelsFromWindow() {
    let { windowStart, windowEnd } = this;
    const { graphLeft, graphMargin, xScale } = this.config;
    if (!isSet(windowStart)) {
      this.startX = this.selectionMin;
      this.endX = this.selectionMax;
      return;
    }
    this.startX = xScale(windowStart) + graphLeft + graphMargin;
    this.endX = xScale(windowEnd) + graphLeft + graphMargin;
  }

  resetPagination() {
    this.dataKeys = [];
    this.dataKeyIndexes = [];
    this.dataKeyCounts = [];
    this._data = [];
    // Index within already bound data
    this.dataIndex = 0;
    // Running domains of [paginated] data
    this.xDomain = extentOne();
    this.yDomain = extentOne();
    // Contains all data for dataKey
    this.dataKeyData = {};
    // Contains only lone nodes for dataKey
    this.dataKeyNodes = {};
    // Contains only null nodes for dataKey
    this.dataKeyNulls = {};
  }

  bindDomain() {

    if (!this.changes.consume(CHANGE_DOMAIN)) {
      return;
    }

    debugTime('domain');

    // Refresh data when needed or add pages without reiteration
    let { paginated } = this.args;
    if (typeof paginated === 'function') {
      paginated = paginated();
    }
    if (!paginated || !this.xDomain) {
      this.resetPagination();
    }
    let {
      dataIndex, xDomain, yDomain,
      dataKeyData, dataKeyNodes, dataKeyNulls,
    } = this;

    const {
      dataKey: _dataKey, dataKeyIndex: indexFn,
      xLowerDomain, xUpperDomain, yLowerDomain, yUpperDomain, onDomain,
    } = this.args;
    const { input, xAxisValue, yAxisValue } = this.config;

    const newData = this.args.data?.arrangedContent || this.args.data || [];
    const dataLen = newData.length;
    const data = this._data;

    // Track data-keys (distinct graph lines), if any
    const dataKeys = this.dataKeys;
    const dataKeyIndexes = this.dataKeyIndexes;
    const dataKeyCounts = this.dataKeyCounts;

    let hasNulls = false;

    // Map data to proxy objects and run accumulative operations
    for (dataIndex; dataIndex < dataLen; ++dataIndex) {
      const d = newData[dataIndex];
      const xValue = xAxisValue(d, dataIndex);
      const yValue = yAxisValue(d, dataIndex);
      const dataKey = call(_dataKey, d[_dataKey], d, dataIndex);

      xDomain = extentOne(xValue, xDomain);
      yDomain = extentOne(yValue, yDomain);

      let isNull = false;
      if (isUnset(xValue) || isUnset(yValue)) {
        isNull = hasNulls = true;
        if (!dataKeyNulls[dataKey]) {
          dataKeyNulls[dataKey] = [];
        }
        dataKeyNulls[dataKey].push(d);
      }

      if (!dataKeyData[dataKey]) {
        dataKeyData[dataKey] = [];
      }

      const allData = dataKeyData[dataKey];
      const nodes = dataKeyNodes[dataKey];
      const nulls = dataKeyNulls[dataKey];

      if (!isNull) {
        if (!allData.length || (
          nulls?.length
            && nulls[nulls.length - 1] === allData[allData.length - 1]
        )) {
          // This is a node if last was a null
          if (!dataKeyNodes[dataKey]) {
            dataKeyNodes[dataKey] = [];
          }
          dataKeyNodes[dataKey].push(d);
        } else if (nodes?.length
          && nodes[nodes.length - 1] === allData[allData.length - 1]
        ) {
          // Last one is not longer a lone node if it can connect to this one
          dataKeyNodes[dataKey].pop();
        }
      }

      allData.push(d);

      let localDataKeyIndex = dataKeys.indexOf(dataKey);
      if (localDataKeyIndex < 0) {
        localDataKeyIndex = dataKeys.length;
        dataKeys[localDataKeyIndex] = dataKey;
        dataKeyIndexes[localDataKeyIndex] = indexFn ? indexFn(dataKey) : localDataKeyIndex;
        dataKeyCounts[localDataKeyIndex] = 1;
      } else {
        ++dataKeyCounts[localDataKeyIndex];
      }
      const dataKeyIndex = dataKeyIndexes[localDataKeyIndex];

      data[dataIndex] = Object.assign({}, {
        dataKey,
        dataKeyIndex,
        localDataKeyIndex,
        xValue,
        yValue,
      });
    }

    const xBoundDomain = [ xLowerDomain ?? xDomain[0], xUpperDomain ?? xDomain[1] ];
    const yBoundDomain = [ yLowerDomain ?? yDomain[0], yUpperDomain ?? yDomain[1] ];

    // Set accumulated values for dependant CPs
    // TODO: check for actual domain component args
    input.xDomain = xBoundDomain;
    input.yDomain = yBoundDomain;

    this._data = data;

    call(onDomain, null, {
      x: xBoundDomain,
      y: yBoundDomain,
    }, data.length, dataKeys, {
      x: xDomain,
      y: yDomain,
    }, hasNulls);

    Object.assign(this, {
      dataIndex, xDomain, yDomain,
      dataKeyData, dataKeyNodes, dataKeyNulls,
    });

    debugTimeEnd('domain');

  }

  calculatePixelIndexes() {

    if (!this.changes.consume(CHANGE_PIXEL_INDEX)) {
      return;
    }

    debugTime('pixel indexes');

    const { sourceGraphRect } = this.args;
    let { graphWidth, graphMargin, xScale } = this.config;
    if (sourceGraphRect) {
      graphWidth = sourceGraphRect.width;
    }
    graphWidth = Math.max(0, graphWidth);
    const fullWidth = Math.ceil(Math.max(0, graphWidth - graphMargin - graphMargin));

    const pixelIndex = new Array(fullWidth);
    const data = this._data || [];
    const dataLen = data.length;
    let lastX = -1;

    // TODO build this into the bindDomain action
    for (let i = 0; i < dataLen; ++i) {
      const d = data[i];
      const isNode = isSet(d.yValue);
      // Note: if we need to display nulls in the future this should be
      // removed, but for now we can skip duplicate adjacent nulls.
      if (!isNode) {
        continue;
      }

      const x = Math.floor(xScale(d.xValue));
      if (!Number.isNaN(x) && x >= 0 && x < pixelIndex.length) {
        while (lastX < x) {
          pixelIndex[++lastX] = [];
        }
        pixelIndex[x][pixelIndex[x].length] = i;
      }
    }
    while (lastX < graphWidth) {
      pixelIndex[++lastX] = [];
    }

    this.pixelIndex = pixelIndex;

    debugTimeEnd('pixel indexes');

  }

  draw() {

    const { args, config, mainCanvas } = this;
    const { width, height } = config;

    const needsRender = this.changes.consume(CHANGE_RENDER, CHANGE_SIZE);
    if (!width || !height || !needsRender) {
      return;
    }

    const { graphMargin, graphWidth, graphHeight } = config;

    // FIXME for this to work there needs to be a way to run unrun tasks on next
    //  frame, or else first run wins, meaning if bindDomain takes too long, then
    //  lines or nodes are never drawn.
    const _maxFrameTime = Infinity; //50;
    const t0 = performance.now();
    const maxFrameTime = () => performance.now() - t0 >= _maxFrameTime;
    const endFrame = () => {
      if (DEBUG) {
        debugLog('Frame exited early');
        debugTimeEnd('draw');
        debugGroupEnd();
      }
    }

    debugGroup(`Graph scroll draw`);
    debugTime('draw');

    this.bindDomain();
    if (maxFrameTime()) {
      return endFrame();
    }

    this.calculatePixelIndexes();
    if (maxFrameTime()) {
      return endFrame();
    }

    const {
      changes,
      _data, dataKeys, dataKeyCounts, dataKeyIndexes, linesZIndex,
      bottomAxisCanvas, lineCanvases, linesEl,
      lineBackgroundElement,
      templateConfig: {
        lineClass,
      },
    } = this;
    const {
      lineMeta,
      dataKeyColorsCyclic, dataKeyColors: colorFn
    } = args;
    const {
      xScale, xAxisOptions, xAxisLeft, xAxisTop, xAxisSize,
      yScale,
      graphLeft, graphTop, lineColor,
      nodeSize,
    } = config;
    const singleNodeSize = nodeSize / 2;
    const singleNodeCenter = singleNodeSize / 2;

    const dataKeysLen = dataKeys.length;

    const ctx = (c, noAlpha) =>
      getContext(c, '2d', noAlpha ? { ...ctxOptions, alpha: false } : ctxOptions);

    const max = (dataKeysLen - (dataKeyColorsCyclic ? 0 : 1)) || 1;

    const data = _data || [];

    const gridHasSize = graphWidth && graphHeight;
    const gridMeta = gridHasSize && changes.consume(CHANGE_LINE_META);

    if (maxFrameTime()) {
      return endFrame();
    }

    debugTime('axes');

    const renderXAxis = xAxisSize.width && xAxisSize.height && changes.consume(CHANGE_X_AXIS);
    if (renderXAxis) {
      Object.assign(bottomAxisCanvas.style, {
        transform: `translate(${xAxisLeft}px, ${xAxisTop}px)`,
      });
      clearCanvas(bottomAxisCanvas, this.timeSeriesOutputs.colorGraph?.xAxisBgColor || '#fff', xAxisSize.width, xAxisSize.height, ctxOptions);
      drawGraphAxis(ctx(bottomAxisCanvas, true), xScale, xAxisOptions);
    }

    debugTimeEnd('axes');

    if (maxFrameTime()) {
      return endFrame();
    }

    debugTime('lines');

    const gridRedraw = gridHasSize && changes.consume(CHANGE_LINE_REDRAW);
    if (gridRedraw || gridMeta) {

      const drawnLineNodes = new Array(dataKeysLen);
      const drawnNodes = [];

      if (gridRedraw) {
        const localKeyCounts = new Array(dataKeysLen).fill(0);
        let hasLastNode = false;

        // TODO replace with pixelIndex properties for top/bottom nodes
        // Convert data to drawable line nodes
        for (let i = 0, len = data.length; i < len; ++i) {
          const { xValue, yValue, localDataKeyIndex } = data[i];
          const isNode = isSet(yValue);
          // Note: if we need to display nulls in the future this should be
          // removed, but for now we can skip duplicate adjacent nulls.
          if (!isNode && !hasLastNode) {
            continue;
          }

          const x = isSet(xValue) ? xScale(xValue) : xValue;
          const y = isSet(yValue) ? yScale(yValue) : yValue;

          if (isNode && !hasLastNode && (
            i >= len - 1 || !isSet(data[i + 1].yValue)
          )) {
            if (!drawnNodes[localDataKeyIndex]) {
              drawnNodes[localDataKeyIndex] = [];
            }
            drawnNodes[localDataKeyIndex].push({ x, y });
            continue;
          }

          hasLastNode = isNode;

          const nodeIndex = localKeyCounts[localDataKeyIndex];
          if (!nodeIndex) {
            drawnLineNodes[localDataKeyIndex] = new Array(dataKeyCounts[localDataKeyIndex]);
          }
          drawnLineNodes[localDataKeyIndex][nodeIndex] = { x, y };
          ++localKeyCounts[localDataKeyIndex];
        }

        if (lineBackgroundElement) {
          resizeStyleRect(lineBackgroundElement.style, graphWidth, graphHeight);
          Object.assign(lineBackgroundElement.style, {
            transform: `translate(${graphLeft}px, ${graphTop}px)`,
          });
        }

        Object.assign(linesEl.style, {
          transform: `translate(${graphLeft}px, ${graphTop}px)`,
        });
      }

      const lineClasses = getClasses(lineClass);

      let nonTooltipLineCount = dataKeysLen;
      let nonTooltipLines = new Array(nonTooltipLineCount);
      let countInc = 0;
      for (let i = 0; i < dataKeysLen && countInc < nonTooltipLineCount; ++i) {
        nonTooltipLines[countInc] = i;
        ++countInc;
      }
      nonTooltipLines.sort((a, b) => dataKeyIndexes[a] - dataKeyIndexes[b]);

      const { xAxisValue, yAxisValue } = this.config;
      let { dataKeyData, dataKeyNodes } = this;

      const scaleBy = (value, scale) => isSet(value) ? scale(value) : value;
      const transform = (d, i) => ({
        x: scaleBy(xAxisValue(d, i), xScale),
        y: scaleBy(yAxisValue(d, i), yScale),
      });

      for (let i = 0; i < dataKeysLen; ++i) {

        const dataKeyIndex = dataKeyIndexes[i];
        const dataKey = this.dataKeys[dataKeyIndex] ?? null;
        let lineCanvas = lineCanvases[i];
        if (!lineCanvas) {
          lineCanvas = lineCanvases[i] = undraggable(createCanvas(0, 0, features.alpha ? null : '#fff', ctxOptions));
          lineCanvas.classList.add(...lineClasses);
          linesEl.appendChild(lineCanvas);
        }
        lineCanvas.style.visibility = dataKeysLen <= 1 || lineMeta?.[dataKeyIndex]?.visible ? '' : 'hidden';

        if (features.lineOrder) {
          lineCanvas.style.zIndex = linesZIndex + i;
        }

        if (gridRedraw) {
          clearCanvas(lineCanvas, features.alpha ? null : '#fff', graphWidth, graphHeight, ctxOptions);

          const lineCtx = ctx(lineCanvas);

          const colorValue = dataKeyIndex / max;
          let c = lineColor;
          if (dataKeysLen > 1 && colorFn) {
            c = colorFn(colorValue);
          }

          drawGraphLines(lineCtx, dataKeyData[dataKey], {
            padding: graphMargin,
            strokeStyle: c,
            contextOptions: ctxOptions,
            transform,
          });
          lineCtx.fillStyle = alpha(c, .5);
          lineCtx.strokeStyle = 'rgba(0, 0, 0, 1)';
          const nodes = dataKeyNodes[dataKey] || [];
          for (let i = 0; i < nodes.length; ++i) {
            const { x, y } = transform(nodes[i], i);
            drawNodeSquare(
              lineCtx,
              x + graphMargin - singleNodeCenter,
              y + graphMargin - singleNodeCenter,
              singleNodeSize
            );
          }
        }
      }

      debugTimeEnd('lines');

      if (maxFrameTime()) {
        return endFrame();
      }

    }

    if (maxFrameTime()) {
      return endFrame();
    }

    debugTime('final draws');
    resizeRect(mainCanvas, width, height, window.devicePixelRatio);
    clearCanvas(mainCanvas, '#fff', width, height, ctxOptions);
    debugTimeEnd('final draws');

    debugTimeEnd('draw');
    debugGroupEnd();
  }

  // Type of drag being performed
  dragType;
  // Mouse x position during drag
  startX = 0;
  endX = 0;
  // Temporary storage in case range is moved
  selectionWidth = 0;
  // Temporary storage in case of drag cancel
  oldStartX = 0;
  oldEndX = 0;
  // Location of mouse within handle
  handleX = 0;
  // Which handle being resized
  handle;

  @cached get selectionMin() {
    const { graphLeft, graphMargin } = this.config;
    return Math.round(graphLeft + graphMargin);
  }

  @cached get selectionMax() {
    const { graphLeft, graphWidth, graphMargin } = this.config;
    return Math.round(graphLeft + graphWidth - graphMargin);
  }

  updateSliderPositions() {
    if (this.dragType === 'outside') {
      return;
    }
    const { startX, endX } = this.getWindowFromSliderPositions();
    const { startHandle, endHandle, sliderRange } = this;
    startHandle.style.left = `${Math.round(startX - startHandle.offsetWidth)}px`;
    endHandle.style.left = `${Math.round(endX)}px`;
    Object.assign(sliderRange.style, {
      left:  `${Math.round(startX)}px`,
      width: `${Math.round(endX - startX)}px`
    });
    once(this, this.setWindowFromSliderPositions);
  }

  cancelDrag(cancel) {
    this.startX = this.oldStartX;
    this.endX = this.oldEndX;
    cancel?.();
  }

  setCursor(type) {
    const classList = document?.body.classList;
    if (!classList) {
      return;
    }
    Object.entries(cursors).forEach(([ key, value ]) => {
      if (key === type) {
        classList.add(value);
      } else {
        classList.remove(value);
      }
    });
  }

  @action onDragStart(event, cancel) {
    const { graphLeft, graphWidth } = this.config;
    const { left } = this.wrapper.getBoundingClientRect();
    const x = (event.changedTouches?.[0] || event).clientX - left;
    let { startX, endX, startHandle, endHandle } = this;

    this._dragTimeout = setTimeout(() => {
      this._dragTimeout = null;
      if (this.dragType === 'outside') {
        this.dragType = 'size';
      }
      this.updateSliderPositions();
    }, this.outsideMouseTimout);

    let start = {
      value: startX,
      handle: startHandle,
      property: 'startX',
    };
    let end = {
      value: endX,
      handle: endHandle,
      property: 'endX',
    };
    if (start.value > end.value) {
      [ start, end ] = [ end, start ];
    }

    this.oldStartX = startX;
    this.oldEndX = endX;

    // If over left handle, drag handle to resize
    if (x <= start.value && x >= start.value - start.handle.offsetWidth) {
      this.dragType = 'resize';
      this.handle = start.property;
      this.handleX = x - start.value;
      this.setCursor('handle');
      return;
    }

    // If over right handle, drag handle to resize
    if (x >= end.value && x <= end.value + end.handle.offsetWidth) {
      this.dragType = 'resize';
      this.handle = 'endX';
      this.handleX = x - end.value;
      this.setCursor('handle');
      return;
    }

    // If over range, drag range to move
    if (x >= start.value && x <= end.value) {
      this.dragType = 'move';
      this.handleX = x - start.value;
      this.selectionWidth = end.value - start.value;
      this.setCursor('range');
      return;
    }

    this.setCursor();

    // Otherwise, ignore dragging outside the graph/handle bounds
    if (x < graphLeft || x > graphLeft + graphWidth) {
      return this.cancelDrag(cancel);
    }

    let { selectionMin, selectionMax } = this;
    this.dragType = 'outside';

    this.startX = this.endX = clamp(selectionMin, x, selectionMax);
    this.updateSliderPositions();
  }

  moveDragTarget(event, drop) {
    const { left } = this.wrapper.getBoundingClientRect();
    const x = (event.changedTouches?.[0] || event).clientX - left;
    let {
      dragType, handle, handleX,
      selectionMin, selectionMax, selectionWidth
    } = this;

    if (dragType === 'outside' || dragType === 'size') {

      if (dragType === 'outside' && !this._dragTimeout) {
        this.dragType = dragType = 'size';
      }

      if (dragType === 'outside' && drop && this.startX === this.endX) {
        if (this.outsideClickMoves) {
          let { oldStartX, oldEndX } = this;
          this.selectionWidth = selectionWidth = Math.abs(oldEndX - oldStartX);
          this.handleX = handleX = selectionWidth / 2
          this.dragType = dragType = 'move'
        } else {
          this.startX = this.selectionMin;
          this.endX = this.selectionMax;
        }
      } else {
        this.endX = clamp(selectionMin, x, selectionMax);
        if (Math.abs(this.startX - this.endX) > this.outsideDragPixels) {
          this.dragType = dragType = 'size';
          if (this._dragTimeout) {
            clearTimeout(this._dragTimeout);
            this._dragTimeout = null;
            this.updateSliderPositions();
          }
        }
      }

    }

    if (dragType === 'move') {

      let rangeX = clamp(selectionMin, x - handleX, selectionMax - selectionWidth);
      if (drop && rangeX === this.oldStartX) {
        this.handleX = handleX = selectionWidth / 2
        rangeX = clamp(selectionMin, x - handleX, selectionMax - selectionWidth);
      }
      this.startX = rangeX;
      this.endX = rangeX + selectionWidth;

    } else if (dragType === 'resize') {

      this[handle] = clamp(selectionMin, x - handleX, selectionMax);

    }
  }

  @action onDragMove(event, cancel) {
    this.moveDragTarget(event);
    this.updateSliderPositions();
  }

  @action onDragEnd(event, canceled) {
    this.setCursor();
    if (canceled) {
      return this.cancelDrag();
    }
    this.moveDragTarget(event, true);
    this.dragType = null;
    this.updateSliderPositions();
  }

  @action onGraphHeightChange() {
    const { config, startHandle, endHandle, sliderRange } = this;
    const { graphHeight, graphTop } = config;
    startHandle.style.top = endHandle.style.top = sliderRange.style.top
      = graphTop + 'px';
    startHandle.style.height = endHandle.style.height = sliderRange.style.height
      = graphHeight + 'px';
  }

  @action drawHandle([ { target } ]) {
    const width = Math.floor(target.offsetWidth);
    const height = Math.floor(target.offsetHeight);
    const east = target === this.endHandle ? 1 : 0;
    const dir = east ? 1 : -1;
    const radius = 6;
    const flatSide = east ? .5 : width - .5;
    const roundSide = east ? width - .5 : .5;
    const roundDist = height / 6;
    const gripOverlap = 0;
    const gripCount = Math.min(Math.floor((width - 4) / 3), 4);
    const gripDist = Math.max(Math.floor(width / (gripCount + 1.5)), 2) * dir;

    const ctx = getContext(target);

    if (width !== target.width || height !== target.width) {
      this.handleWidth = width;
      target.width = width;
      target.height = height;
    } else {
      ctx.clearRect(0, 0, width, height)
    }

    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#000';

    ctx.beginPath();
    ctx.moveTo(flatSide, roundDist);
    ctx.arcTo(roundSide, roundDist, roundSide, roundDist + radius, radius);
    ctx.lineTo(roundSide, height - roundDist - radius);
    ctx.arcTo(roundSide, height - roundDist, flatSide, height - roundDist, radius);
    ctx.lineTo(flatSide, height - roundDist);
    ctx.moveTo(flatSide, 0);
    ctx.lineTo(flatSide, height);
    ctx.closePath();
    ctx.fill();

    for (let i = 0, x = flatSide + gripDist; i < gripCount; ++i, x = x + gripDist) {
      ctx.moveTo(x, roundDist + radius - gripOverlap);
      ctx.lineTo(x, height - roundDist - radius + gripOverlap);
    }

    ctx.stroke();
  }

  @action onHandleSizeChange(entries) {
    this.drawHandle(entries);
    const [ { target } ] = entries;
    const width = Math.floor(target.offsetWidth);
    const height = Math.floor(target.offsetHeight);
    this.args.onHandleSizeChange?.({ width, height });
  }

  <template>
    {{#let
      (classModConfig "c-graph-scroll")
      (passProperties this.config.input)
      as | classModCfg config |
    ~}}
    {{#let
      (classModConfig (classMod config=classModCfg suffix="__line"))
      as | lineClassConfig |
    ~}}
      {{#if @data.length}}
        <div class="c-graph-scroll__hint">
          Drag handles to change timeframe displayed in main plot
        </div>
      {{/if}}
      <div ...attributes
        class={{classMod ""
          (not @data.length) "--empty"
          config=classModCfg
        }}
        {{config "data" @data}}
        {{config "width" @width}}
        {{config "height" @height}}
        {{config "xLabel" @xLabel}}
        {{config "xKey" @xKey}}
        {{config "xTickAngle" @xTickAngle}}
        {{config "yKey" @yKey}}
        {{config "lineColor" @lineColor}}
        {{config "tickArguments" @tickArguments}}
        {{config "tickFormat" @tickFormat}}
        {{config "fontSize" @fontSize}}
        {{config "fontColor" @fontColor}}
        {{config "nodeSize" @nodeSize}}
        {{config "hideYAxis" true}}
        {{config "paddingLeft" (max this.handleWidth @marginLeft)}}
        {{config "paddingRight" (max this.handleWidth @marginRight)}}
        {{didInsert this.didInsertWrapper (hash
          hoverClass=(classMod config=classModCfg suffix="--hover")
          linesClass=(classMod config=classModCfg suffix="__lines")
          lineClass=(classMod config=lineClassConfig)
          axisClass=(classMod config=classModCfg suffix="__axis")
          bottomAxisClass=(classMod config=classModCfg suffix="__axis--bottom")
          tooltipsClass=(classMod config=classModCfg suffix="__tooltips")
          tooltipClass=(classMod config=classModCfg suffix="__tooltip"))
        }}
        {{update this.onSizeChange2 this.config.width this.config.height}}
        {{update this.onSizeChange this.config.width this.config.height}}
        {{update this.onConfigChange this.config}}
        {{update this.onWidthChange this.config.width}}
        {{update this.onHeightChange this.config.height}}
        {{update this.onGraphHeightChange this.config.graphHeight this.config.padding}}
        {{update this.onDataChange (emberArray @data)}}
        {{update this.onScaleChange this.config.xScale this.config.yScale}}
        {{update this.onDataKeyColorChange @dataKeyColor}}
        {{update this.onLineMetaChange (emberArray @lineMeta)}}
        {{update this.onSourceGraphRectChange @sourceGraphRect}}
        {{onDrag this.onDragStart this.onDragMove this.onDragEnd}}
      >
        <canvas role="slider" data-slider-handle="start" {{onResize this.onHandleSizeChange}} />
        <div role="slider" data-slider-range />
        <canvas role="slider" data-slider-handle="end" {{onResize this.onHandleSizeChange}} />
      </div>
      {{yield}}
    {{/let}}
    {{/let}}
  </template>
}
